import { addMinutes, toYMDHMS, toUTC } from '@/js/shared/utils';
import { formatString, trimChar, isNotNullOrWhiteSpace } from '@/js/shared/utils';
import { toFixedNumber } from '@/js/shared/utils';

function arrayToDictionary(arr) {
	var dic = {};

	for (var i = 0; i < arr.length; i++) {
		dic[arr[i]] = true;
	}

	return dic;
}

export function getUrlParameterByName(name, url) {
	if (!url) url = window.location.href;
	name = name.replace(/[\[\]]/g, '\\$&');
	var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
	var results = regex.exec(url);
	if (!results) return null;
	if (!results[2]) return '';
	return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export function getUrlSubDirByIndex(index, url) {
	if (!url) url = window.location.pathname;
	url = trimChar(url, '/');
	var sections = url.split('/');
	return (index < sections.length)
		? sections[index]
		: null;
}

export function bytesToMegabytesString(nrBytes, decimals = 2) {
	var megabytes = toFixedNumber((nrBytes / 1024 / 1024), decimals);
	return formatString('{0} MB', megabytes);
}

export function getClientDateTimeString() {
	return toYMDHMS(new Date());
}

export function getPropNameByIndex(obj, i) {
	var keys = Object.keys(obj);

	return (i < keys.length)
		? keys[i]
		: null;
}

export function getPropValueByIndex(obj, i) {
	var keys = Object.keys(obj);

	return (i < keys.length)
		? obj[keys[i]]
		: null;
}

export function parseWidthValue(valWidth) {
	var inputIsString = (typeof valWidth === 'string');
	var number = parseInt(valWidth);
	var validNumber = !Number.isNaN(number);
	var endsWithPx = (inputIsString) ? valWidth.endsWith('px') : false;
	var endsWithPct = (inputIsString) ? valWidth.endsWith('%') : false;
	var endsWithNothing = (!endsWithPx && !endsWithPct);

	return (validNumber && (endsWithPx || endsWithPct))
		? valWidth
		: (validNumber && endsWithNothing)
			? `${number}px`
			: '';
}

export function deepCopy(obj, ...propsToKeep) {
	var copy = JSON.parse(JSON.stringify(obj));

	if (propsToKeep.length === 0) {
		return copy;
	}

	var dic = arrayToDictionary(propsToKeep);

	for (var prop in copy) {
		if (!dic[prop]) {
			delete copy[prop];
		}
	}

	return copy;
}

export function getNrSecsUntilSerializedUtcEnd(serializedUtcEnd) {
	var utcBegin = toUTC(new Date());
	var utcEnd = new Date(serializedUtcEnd);
	var nrSeconds = ((utcEnd - utcBegin) / 1000);
	return nrSeconds;
}

export function allNumbersEqual(numbers) {
	return (numbers?.length > 0)
		? numbers.every((val, i, arr) => (val === arr[0]))
		: true;
}

export function lastNumbersEqual(numbers, equalCount) {
	var enoughNumbers = (numbers.length >= equalCount);

	return (enoughNumbers)
		? allNumbersEqual(numbers.slice(numbers.length - equalCount))
		: false;
}

export function versionedImageUrl(url, version) {
	return (url && version)
		? `${url}?v=${version}`
		: url;
}

export function debounce(func, timeout) {
	var timer = null;

	var debounced = function (...args) {
		var that = this;

		clearTimeout(timer);
		timer = setTimeout(() => {
			timer = null;
			func.apply(that, args);
		}, timeout);
	};

	debounced.cancel = () => {
		clearTimeout(timer);
		timer = null;
	};

	return debounced;
}

export function isAlphaNumeric(str) {
	return str.match(/^[0-9a-zA-Z]+$/);
}

export function isAlphaNumericChar(char) {
	return char.match(/^[0-9a-zA-Z]$/);
}

export function toCustomBase64(obj) {
	var success = false;
	var b64 = '';

	try {
		var str = JSON.stringify(obj);
		b64 = btoa(str).replace(/\//g, '_').replace(/\+/g, '-');
		success = true;
	} catch (e) {
	}

	return { success : success, b64 : b64 };
}

export function fromCustomBase64(str) {
	var success = false;
	var obj = null;

	try {
		str = str.replace(/_/g, '/').replace(/\-/g, '+');
		var _obj = atob(str);
		obj = JSON.parse(_obj);
		success = true;
	} catch (e) {
	}

	return { success : success, obj : obj };
}

export function dicWhere(dic, funcWhere) {
	var dicSubSet = {};

	for (const [key, val] of Object.entries(dic)) {
		if (funcWhere(val)) {
			dicSubSet[key] = val;
		}
	}

	return dicSubSet;
}

export function getStarImageUrls(rating) {
	var stars = Math.round(rating * 2) / 2;
	var nrFullStars = (stars / 1) >> 0;
	var nrHalfStars = ((stars - nrFullStars) / 0.5);
	var nrEmptyStars = (5 - nrFullStars - nrHalfStars);
	var starValues = [];

	for (var i = 0; i < nrFullStars; i++) {
		starValues.push('/img/icons/star-full.svg');
	}

	for (var j = 0; j < nrHalfStars; j++) {
		starValues.push('/img/icons/star-half.svg');
	}

	for (var k = 0; k < nrEmptyStars; k++) {
		starValues.push('/img/icons/star-empty.svg');
	}

	return starValues;
}

export function executeAtIntervals(funcsTimeouts) {
	for (var i = 0; i < funcsTimeouts.length; i++) {
		var func = funcsTimeouts[i].func;
		var timeout = funcsTimeouts[i].timeout;
		setTimeout(func, timeout);
	}
}

export function randomString(length = 32) {
	const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	const lenChars = chars.length;
	let randomChars = [];

	for (let i = 0; i < length; i++) {
		randomChars.push(chars[Math.floor(Math.random() * lenChars)]);
	}

	return randomChars.join('');
}

export function isErrorObject(obj) {
	return Object.prototype.toString.call(obj) === '[object Error]';
}

export function stringifySafely(obj) {
	var result;

	try {
		result = JSON.stringify(obj);
	} catch (e) {
		result = e.toString();
	}

	return result;
}

export function toInt(value, defaultValue = 0) {
	value = (value) ? parseFloat(value) : defaultValue;
	return (!isNaN(value)) ? value : defaultValue;
}

export function toFloat(value, defaultValue = 0) {
	value = (value) ? parseFloat(value) : defaultValue;
	return (!isNaN(value)) ? value : defaultValue;
}

export { isNotNullOrWhiteSpace };